import { FunctionComponent, useEffect, useState } from 'react'
import EditProfilePage from './EditProfilePage'
import { Button } from 'react-bootstrap'
import { getUserInfo } from 'Frontend/scripts/services/profile.service'
import { IProfileUser } from 'Frontend/scripts/models/profile.model'
import { getMyWorkrooms } from 'Frontend/scripts/services/workrooms.service'
import { IWorkroom } from 'Frontend/scripts/models/workrooms.model'
import { convertDate, convertDateAndTime } from 'Frontend/scripts/convertDates'
import { useTranslation } from 'react-i18next'

interface ProfileProps {
  userId: string
  hasEditAccess: string
  showMyWorkrooms: string
}

const ProfileInfo: FunctionComponent<ProfileProps> = ({ userId, hasEditAccess, showMyWorkrooms }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [user, setUser] = useState<IProfileUser>()
  const [workrooms, setWorkrooms] = useState<IWorkroom[]>([])
  const [userHasUpdated, setUserHasUpdated] = useState<boolean>(true)
  const { t } = useTranslation()
  const showMyWorkroomsConverted = showMyWorkrooms === 'true' || showMyWorkrooms === 'True'
  const hasEditAccessConverted = hasEditAccess === 'true' || hasEditAccess === 'True'

  useEffect(() => {
    showMyWorkroomsConverted &&
      getMyWorkrooms().then((workroomsData: IWorkroom[]) => {
        setWorkrooms(workroomsData)
      })
  }, [])

  useEffect(() => {
    userHasUpdated &&
      getUserInfo(parseInt(userId)).then((userData: IProfileUser) => {
        setUser(userData)
        setUserHasUpdated(false)
      })
  }, [userHasUpdated])

  return (
    <>
      {isEditing ? (
        <EditProfilePage
          user={user as IProfileUser}
          callback={() => {
            setIsEditing(false)
            setUserHasUpdated(true)
          }}
        />
      ) : (
        <>
          <div className="livstecknet__colored-area mb-3">
            <div className="d-flex flex-column flex-xl-row gap-3">
              {user?.profileImageUrl && (
                <div className="profile-page__image-container">
                  <img className="profile-page__image" src={user?.profileImageUrl} alt=""></img>
                </div>
              )}
              <div className="w-100">
                <h1 className="profile-page__heading">
                  {user?.firstname} {user?.lastname}
                </h1>
                <div className="row gy-1">
                  {user?.email && (
                    <>
                      <div className="col-sm-5 col-xl-4 col-12">
                        <label>
                          <span className="fa fa-envelope me-2 workrooms__members-card-icon workrooms__members-card-icon--medium"></span>
                          {t('official-email')}
                        </label>
                      </div>
                      <div className="col-sm-7 col-md-7 col-xl-8 col-12">
                        <a href={`mailto:${user?.email}`} className="text-truncate d-block">
                          {user?.email}
                        </a>
                      </div>
                    </>
                  )}
                  {user?.phone && (
                    <>
                      <div className="col-sm-5 col-xl-4 col-12">
                        <label>
                          <span className="fa fa-phone me-2 workrooms__members-card-icon"></span>
                          {t('phone')}
                        </label>
                      </div>
                      <div className="col-sm-7 col-md-7 col-xl-8 col-12">
                        <a href={`tel:${user?.phone}`}>{user?.phone}</a>
                      </div>
                    </>
                  )}
                  {user?.mobilePhone && (
                    <>
                      <div className="col-sm-5 col-xl-4 col-12">
                        <label className="d-flex align-items-center">
                          <span className="fa fa-mobile-phone me-2 workrooms__members-card-icon workrooms__members-card-icon--large"></span>
                          {t('mobile-phone')}
                        </label>
                      </div>

                      <div className="col-sm-7 col-md-7 col-xl-8 col-12">
                        <a href={`tel:${user?.mobilePhone}`}>{user?.mobilePhone}</a>
                      </div>
                    </>
                  )}
                  {user?.postalAddress && (
                    <>
                      <div className="col-sm-5 col-xl-4 col-12">
                        <label>
                          <span className="fa fa-home me-2 workrooms__members-card-icon"></span>
                          {t('postal-code')}
                        </label>
                      </div>
                      <div className="col-sm-7 col-md-7 col-xl-8 col-12">
                        <p className="mb-0">{user?.postalAddress}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="livstecknet__colored-area mb-3">
            <h2 className="profile-page__section-heading">{t('competence-areas')}</h2>
            <div className="row gy-1">
              {user?.competenceAreas && (
                <>
                  <div className="col-sm-5 col-xl-4 col-12">
                    <label>{t('competences')}</label>
                  </div>
                  <div className="col-sm-7 col-md-7 col-xl-8 col-12">
                    {user?.competenceAreas.map((competence) => (
                      <p key={`competence-${competence.id}`} className="mb-0">
                        {competence.name}
                      </p>
                    ))}
                  </div>
                </>
              )}
              {user?.authority && (
                <>
                  <div className="col-sm-5 col-xl-4 col-12">
                    <label>{t('authority')}</label>
                  </div>
                  <div className="col-sm-7 col-md-7 col-xl-8 col-12">
                    <p className="mb-0">{user?.authority}</p>
                  </div>
                </>
              )}
              {user?.municipalities && (
                <>
                  <div className="col-sm-5 col-xl-4 col-12">
                    <label>{t('municipality')}</label>
                  </div>
                  <div className="col-sm-7 col-md-7 col-xl-8 col-12">
                    {user?.municipalities.map((municipality) => (
                      <p key={`municipality-${municipality.id}`} className="mb-0">
                        {municipality.name}
                      </p>
                    ))}
                  </div>
                </>
              )}
              {user?.county && (
                <>
                  <div className="col-sm-5 col-xl-4 col-12">
                    <label>{t('county')}</label>
                  </div>
                  <div className="col-sm-7 col-md-7 col-xl-8 col-12">
                    <p className="mb-0">{user?.county.name}</p>
                  </div>
                </>
              )}
              {user?.lastLoggedIn && (
                <p className="mb-0 mt-4">
                  {t('latest-signed-in')} {convertDate(user?.lastLoggedIn)}
                </p>
              )}
            </div>
          </div>

          {showMyWorkroomsConverted && (
            <div className="livstecknet__colored-area">
              <h2 className="profile-page__section-heading">{t('my-workrooms')}</h2>
              <div className="d-flex flex-wrap gap-3">
                {workrooms.map((workroom) => (
                  <a key={`workroom-id-${workroom.id}`} href={workroom.url}>
                    {workroom.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {!isEditing && hasEditAccessConverted && (
        <div className="d-flex justify-content-end">
          <Button variant="secondary" className="mt-3" onClick={() => setIsEditing(!isEditing)}>
            {t('edit')}
          </Button>
        </div>
      )}
    </>
  )
}
export default ProfileInfo
